import { object } from 'yup';

import { formatDateForInput, formatForWire, formatZonedDateForInput } from 'utilities/common';
import { getDistance } from 'utilities/googleMapsClient';
import Log from 'utilities/log';

import { NULLABLE_STRING, plusMinusFiveYearsDateSchema } from 'modules/common/constants';
import { afterAuthDateSchema } from '../constants';
import Contact from './contact';
import Location from './location';

export default class PrimaryInformation {
  static schema = object().shape({
    transferee: Contact.schema,
    departureLocation: Location.schema,
    destinationLocation: Location.schema,
    relocationPolicyCodeId: NULLABLE_STRING,
    intakeRecordStatusId: NULLABLE_STRING,
    newJobStartDate: plusMinusFiveYearsDateSchema.concat(afterAuthDateSchema),
    mtAuthorizationId: NULLABLE_STRING,
  });

  constructor(wireData, countries = [], provinces = []) {
    const json = wireData || {};
    this.transferee = json.transferee;
    this.relocationPolicyCodeId = json.relocationPolicyCodeId;
    this.intakeRecordStatusId = json.intakeRecordStatusId;
    this.relocationPolicyCodes = json.relocationPolicyCodes ? this.sortPolicyCodes(json.relocationPolicyCodes) : [];
    this.relocationDistance = json.relocationDistance;
    this.newJobStartDate = formatZonedDateForInput(json.newJobStartDate);
    this.mtAuthorizationId = json.mtAuthorizationId;
    
    // Store province/country GUID fields for V2 API persistence
    this.departureProvinceId = json.departureProvinceId || null;
    this.departureCountryId = json.departureCountryId || null;
    this.destinationProvinceId = json.destinationProvinceId || null;
    this.destinationCountryId = json.destinationCountryId || null;
    
    this.departureLocation = new Location({
      city: json.departureCity,
      state: json.departureStateCode,
      country: json.departureCountryCode,
      provinceId: json.departureProvinceId,
      countryId: json.departureCountryId,
    }, countries, provinces);
    this.destinationLocation = new Location({
      city: json.destinationCity,
      state: json.destinationStateCode,
      country: json.destinationCountryCode,
      provinceId: json.destinationProvinceId,
      countryId: json.destinationCountryId,
    }, countries, provinces);
    this.estimatedStartDate = formatDateForInput(json.estimatedStartDate);
    this.estimatedEndDate = formatDateForInput(json.estimatedEndDate);
    this.departureAddressComment = json.departureAddressComment;
    this.destinationInformationComment = json.destinationInformationComment;
  }

  sortPolicyCodes = (relocationPolicyCodes) => {
    const s = relocationPolicyCodes.sort((a, b) => { 
      if ((a.active && a.webViewable) && (!b.active || !b.webViewable)) return -1;
      if ((!a.active || !a.webViewable) && (b.active && b.webViewable)) return 1;
      return 0;
    });
    return s;
  }

  static fromWireData(wireData, countries = [], provinces = []) {
    return new PrimaryInformation(wireData, countries, provinces);
  }

  static generateErrors(errors) {
    if (!errors) {
      return null;
    }

    return {
      ...errors,
      departureLocation: Location.generateErrors(errors, 'departure') ? 'Invalid/missing address' : null,
      destinationLocation: Location.generateErrors(errors, 'destination') ? 'Invalid/missing address' : null,
    };
  }

  async calculateRelocationDistance() {
    try {
      const { value } = await getDistance(this.departureLocation, this.destinationLocation);
      this.relocationDistance = value;
    } catch (err) {
      Log.error('Unable to calculate distance', err);
    }
  }

  toWireData(countries = [], provinces = []) {
    // Convert state/country codes to GUIDs for V2 API
    // If user changed dropdowns, find the new GUIDs; otherwise use stored values
    let departureProvinceId = this.departureProvinceId;
    let departureCountryId = this.departureCountryId;
    let destinationProvinceId = this.destinationProvinceId;
    let destinationCountryId = this.destinationCountryId;

    // Lookup departure country from code
    if (this.departureLocation.country && countries.length > 0) {
      const deptCountry = countries.find(c => 
        c.countryCode === this.departureLocation.country || 
        c.CountryCode === this.departureLocation.country
      );
      if (deptCountry) {
        departureCountryId = deptCountry.id || deptCountry.Id;
      }
    }

    // Lookup departure province from code
    if (this.departureLocation.state && provinces.length > 0) {
      const deptProvince = provinces.find(p => 
        p.code === this.departureLocation.state || 
        p.Code === this.departureLocation.state
      );
      if (deptProvince) {
        departureProvinceId = deptProvince.id || deptProvince.Id;
      }
    }

    // Lookup destination country from code
    if (this.destinationLocation.country && countries.length > 0) {
      const destCountry = countries.find(c => 
        c.countryCode === this.destinationLocation.country || 
        c.CountryCode === this.destinationLocation.country
      );
      if (destCountry) {
        destinationCountryId = destCountry.id || destCountry.Id;
      }
    }

    // Lookup destination province from code
    if (this.destinationLocation.state && provinces.length > 0) {
      const destProvince = provinces.find(p => 
        p.code === this.destinationLocation.state || 
        p.Code === this.destinationLocation.state
      );
      if (destProvince) {
        destinationProvinceId = destProvince.id || destProvince.Id;
      }
    }
    
    return formatForWire({
      transferee: this.transferee.toWireData(),
      departureLocation: this.departureLocation.toWireData(),
      destinationLocation: this.destinationLocation.toWireData(),
      relocationPolicyCodeId: this.relocationPolicyCodeId,
      intakeRecordStatusId: this.intakeRecordStatusId,
      relocationPolicyCodes: this.relocationPolicyCodes,
      relocationDistance: this.relocationDistance,
      newJobStartDate: this.newJobStartDate,
      mtAuthorizationId: this.mtAuthorizationId,
      estimatedStartDate: this.estimatedStartDate,
      estimatedEndDate: this.estimatedEndDate,
      departureAddressComment: this.departureAddressComment,
      destinationInformationComment: this.destinationInformationComment,
      // Include province/country GUID fields for V2 API
      departureProvinceId,
      departureCountryId,
      destinationProvinceId,
      destinationCountryId,
    });
  }
}
